import { MsalProvider } from '@azure/msal-react';
import { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { UserContextProvider } from 'context/user.context';
import { Logged } from 'layout/Logged';
import createEmotionCache from 'lib/emotionCache';
import { SSOInstance } from 'services/azure.service';
import { light } from 'themes';

type MyAppProps = AppProps & {
  emotionCache?: EmotionCache;
};

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();
// global css
const globalCss = {
  html: { height: '100%' },
  body: {
    height: '100%',
    margin: 0,
    padding: 0,
    width: '100%',
    minHeight: '100%',
    background: 'blue',
  },
  'body > div:first-child': {
    minHeight: '100%',
  },
};

function MyApp({
  Component,
  pageProps,
  emotionCache = clientSideEmotionCache,
}: MyAppProps) {
  const router = useRouter();

  return (
    <CacheProvider value={emotionCache}>
      <GlobalStyles styles={globalCss} />
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
      </Head>
      <MsalProvider instance={SSOInstance}>
        <UserContextProvider>
          <ThemeProvider theme={light}>
            <CssBaseline />
            {router.pathname === '/login' ? (
              <Component {...pageProps} />
            ) : (
              <Logged>
                <Component {...pageProps} />
              </Logged>
            )}
          </ThemeProvider>
        </UserContextProvider>
      </MsalProvider>
    </CacheProvider>
  );
}

export default MyApp;
